import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 3rem;
  font-size: 2.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const ContactIcon = styled.div`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  flex-shrink: 0;
`;

const ContactInfo = styled.div`
  h3 {
    color: ${props => props.theme.colors.text};
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    margin: 0;
  }
  
  @media (max-width: 768px) {
    h3 {
      font-size: 1.1rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const ContactForm = styled.form`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 1rem;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  min-height: 120px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.65rem;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #8B5A96, #A569BD);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const SectionHeader = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 1.5rem;
  }
`;

const BusinessHoursBox = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: ${props => props.theme.colors.background};
  border-radius: 10px;
  
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    margin: 0.5rem 0;
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const FAQSection = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  
  h2 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 2rem;
    text-align: center;
  }
`;

const FAQItem = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const FAQQuestion = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
`;

const FAQAnswer = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;
`;

const faqItems = [
  {
    question: "What do I include in the full story?",
    answer: "This is where you could tell the story of how the venture started and the vision you have for where it's going. An important part to include is what you think you need to get the venture moving forward. This will be the bulk of the information people see when seeing your post."
  },
  {
    question: "Can I include personal details in my post?",
    answer: "Due to the POPIA act, you cannot include any personal or unique business information in your post. Unique information includes contact details, business names, addresses, bank details or social details."
  },
  {
    question: "What must I put in the description if I can't include the business title?",
    answer: "You can include a brief description of what the business does. Think of it like a category. For example, 'Home-made organic stuffed cookies'"
  },
  {
    question: "What if I don't know what skill I need to grow my venture?",
    answer: "Select the closest skill you think may match what your venture needs or a skill you think you don't have but the venture would benefit from."
  }
];

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Create mailto link with form data
    const subject = encodeURIComponent(formData.subject || 'Contact Form Submission');
    const body = encodeURIComponent(
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Subject: ${formData.subject || 'N/A'}\n\n` +
      `Message:\n${formData.message}`
    );
    
    const mailtoLink = `mailto:hustlingza@gmail.com?subject=${subject}&body=${body}`;
    
    // Open default email client
    window.location.href = mailtoLink;
    
    toast.success('Opening your email client... Please send the email to complete your message.');
    
    // Clear form after a short delay
    setTimeout(() => {
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 1000);
  };

  return (
    <Container>
      <Title>Get in Touch</Title>
      
      <ContactGrid>
        <ContactCard>
          <SectionHeader>Contact Information</SectionHeader>
          
          <ContactItem>
            <ContactIcon>
              <FaEnvelope />
            </ContactIcon>
            <ContactInfo>
              <h3>Email</h3>
              <p>hustlingza@gmail.com</p>
            </ContactInfo>
          </ContactItem>

          <ContactItem>
            <ContactIcon>
              <FaPhone />
            </ContactIcon>
            <ContactInfo>
              <h3>Phone</h3>
              <p>+27 83 298 8388</p>
            </ContactInfo>
          </ContactItem>

          <ContactItem>
            <ContactIcon>
              <FaMapMarkerAlt />
            </ContactIcon>
            <ContactInfo>
              <h3>Location</h3>
              <p>Cape Town, South Africa</p>
            </ContactInfo>
          </ContactItem>
        </ContactCard>

        <ContactForm onSubmit={handleSubmit}>
          <SectionHeader>Send us a Message</SectionHeader>
          
          <FormGroup>
            <Label>Name *</Label>
            <Input
              type="text"
              placeholder="Your full name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Email *</Label>
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Subject</Label>
            <Input
              type="text"
              placeholder="What is this regarding?"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
            />
          </FormGroup>

          <FormGroup>
            <Label>Message *</Label>
            <TextArea
              placeholder="Tell us how we can help you..."
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
            />
          </FormGroup>

          <SubmitButton type="submit">
            <FaPaperPlane />
            Send Message
          </SubmitButton>
        </ContactForm>
      </ContactGrid>

      <FAQSection>
        <h2>Frequently Asked Questions</h2>
        {faqItems.map((item, index) => (
          <FAQItem key={index}>
            <FAQQuestion>{item.question}</FAQQuestion>
            <FAQAnswer>{item.answer}</FAQAnswer>
          </FAQItem>
        ))}
        <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: `1px solid ${props => props.theme.colors.border}`, textAlign: 'center' }}>
          <p style={{ color: '#666', marginBottom: '0' }}>
            For urgent matters or business partnerships, please email us directly at 
            <strong> hustlingza@gmail.com</strong>
          </p>
        </div>
      </FAQSection>
    </Container>
  );
};

export default ContactUs;